import { getReportFooterText } from '../reportFooter';
export interface TransactionCompletionHeaderInfo {
  fromDate?: string | null;
  toDate?: string | null;
  userEmail?: string | null;
  averageDurationLabel?: string | null;
}

export interface TransactionCompletionItem {
  user: string;
  elapsedLabel: string;
  transactionDate: string;
  transactionTypeLabel: string;
}

export interface TransactionCompletionTemplateOptions {
  generatedAt: Date;
  header: TransactionCompletionHeaderInfo;
  items: TransactionCompletionItem[];
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeText = (value: string | null | undefined): string => {
  const trimmed = value?.toString().trim();
  if (!trimmed) {
    return '—';
  }
  return escapeHtml(trimmed);
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return sanitizeText(value);
  }
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const formatDateTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(value);

const buildItemsTable = (items: TransactionCompletionItem[]): string => {
  if (items.length === 0) {
    return `
      <tr>
        <td colspan="4" class="empty-row">
          No transactions matched the selected filters.
        </td>
      </tr>
    `;
  }

  return items
    .map(
      (item) => `
        <tr>
          <td>${sanitizeText(item.user)}</td>
          <td class="center">${sanitizeText(item.elapsedLabel)}</td>
          <td class="center">${sanitizeText(item.transactionDate)}</td>
          <td class="center">${sanitizeText(item.transactionTypeLabel)}</td>
        </tr>
      `,
    )
    .join('');
};

export const buildTransactionCompletionReportHtml = ({
  generatedAt,
  header,
  items,
}: TransactionCompletionTemplateOptions): string => {
  const generatedLabel = formatDateTime(generatedAt);
  const fromDate = formatDate(header.fromDate ?? null);
  const toDate = formatDate(header.toDate ?? null);
  const userLabel = header.userEmail ? sanitizeText(header.userEmail) : 'All';
  const averageLabel = header.averageDurationLabel
    ? sanitizeText(header.averageDurationLabel)
    : '—';
  const itemsTable = buildItemsTable(items);

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>Transaction Completion Report</title>
    <style>
      :root {
        color-scheme: light;
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 16px;
        font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        color: #15263c;
        background: #ffffff;
      }

      header {
        margin-bottom: 24px;
      }

      .brand {
        font-size: 14px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #3a82f6;
        font-weight: 600;
      }

      .title {
        font-size: 28px;
        font-weight: 700;
        margin: 0;
      }

      .generated {
        margin-top: 4px;
        font-size: 14px;
        color: #4a5c72;
      }

      .filters-row {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        margin-top: 16px;
        margin-bottom: 16px;
      }

      .filter-block {
        min-width: 220px;
      }

      .filter-label {
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #5c6f85;
        margin-bottom: 2px;
      }

      .filter-value {
        font-size: 14px;
        color: #15263c;
      }

      .summary-row {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        margin: 16px 0;
        padding: 8px 12px;
        background: #e9f1ff;
        border-radius: 4px;
        font-size: 14px;
        font-weight: 600;
        color: #15263c;
      }

      .summary-row span {
        margin-right: 16px;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 24px;
        border: 1px solid #d7e3f4;
      }

      thead th {
        background: #e9f1ff;
        text-align: left;
        font-size: 12px;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        padding: 10px 12px;
        color: #4a5c72;
        border-bottom: 1px solid #d7e3f4;
      }

      tbody td {
        padding: 12px;
        border-bottom: 1px solid #edf3fc;
        font-size: 14px;
        color: #1f2f45;
      }

      tbody tr:last-child td {
        border-bottom: none;
      }

      tbody tr:nth-child(even) td {
        background: #f9fbff;
      }

      .numeric {
        text-align: right;
        font-variant-numeric: tabular-nums;
      }

      .center {
        text-align: center;
        font-variant-numeric: tabular-nums;
      }

      .empty-row {
        text-align: center;
        font-style: italic;
        color: #6b7c92;
        padding: 24px 12px;
      }

      footer {
        margin-top: 32px;
        font-size: 12px;
        color: #5c6f85;
        text-align: center;
      }

      @media print {
        body {
          margin: 8mm;
        }
      }
    </style>
  </head>
  <body>
    <header>
      <div class="brand">eQuip Transaction Suite</div>
      <h1 class="title">Transaction Completion Report</h1>
      <div class="generated">Generated ${escapeHtml(generatedLabel)}</div>

      <div class="filters-row">
        <div class="filter-block">
          <div class="filter-label">Start Date</div>
          <div class="filter-value">${fromDate}</div>
        </div>
        <div class="filter-block">
          <div class="filter-label">End Date</div>
          <div class="filter-value">${toDate}</div>
        </div>
        <div class="filter-block">
          <div class="filter-label">User</div>
          <div class="filter-value">${userLabel}</div>
        </div>
      </div>

      <div class="summary-row">
        <span>Average Time To Complete Transaction:</span>
        <span>${averageLabel}</span>
      </div>
    </header>

    <section>
      <table>
        <thead>
          <tr>
            <th>User</th>
            <th class="center">Elapsed Time</th>
            <th class="center">Date of Transaction</th>
            <th class="center">Issue / Turn In / DX</th>
          </tr>
        </thead>
        <tbody>
          ${itemsTable}
        </tbody>
      </table>
    </section>

    <footer>
      Generated ${escapeHtml(generatedLabel)} · ${getReportFooterText()}
    </footer>
  </body>
</html>`;
};
